import React, { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useApp } from "@/context/AppContext";

// The verified badge only ever appears for verified accounts, so its presence
// already means "verified/trusted". The COLOUR therefore encodes the account ROLE:
//  - Prédicateur  → vert Yalola  (autorité spirituelle)
//  - Église/Staff → vert Yalola  (compte officiel / équipe)
//  - Artiste      → violet
//  - Influenceur  → bleu
//  - Membre       → gris ardoise (vérifié, sans rôle spécifique)
export const VERIFIED_ROLE_META = {
  preacher: { color: "var(--yalola-secondary)", key: "role.preacher", fallback: "Prédicateur" },
  artist: { color: "#8B5CF6", key: "role.artist", fallback: "Artiste" },
  influencer: { color: "#3B82F6", key: "role.influencer", fallback: "Influenceur(e)" },
  church: { color: "var(--yalola-secondary)", key: "badge.church", fallback: "Église officielle" },
  staff: { color: "var(--yalola-secondary)", key: "badge.staff", fallback: "Équipe Yalola" },
  member: { color: "#64748B", key: "badge.member", fallback: "Membre vérifié" },
};

export const verifiedRole = (user) => {
  if (!user || !user.is_verified) return null;
  if (user.is_church) return "church";
  if (user.role === "super_admin" || user.role === "manager") return "staff";
  if (user.role === "preacher") return "preacher";
  if (user.role === "artist") return "artist";
  if (user.role === "influencer") return "influencer";
  return "member";
};

export const VerifiedBadge = ({ user, size = 14, interactive = true }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const role = verifiedRole(user);
  if (!role) return null;
  const meta = VERIFIED_ROLE_META[role];
  const roleLabel = t(meta.key, meta.fallback);
  const title = `${roleLabel} · ${t("badge.verified", "vérifié")}`;

  const icon = (
    <ShieldCheck
      size={size}
      className="inline-block shrink-0 align-middle"
      style={{ color: meta.color }}
      data-testid="verified-badge"
      data-role={role}
      aria-label={title}
      title={title}
    />
  );

  if (!interactive) return icon;

  const toggle = (e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          onClick={toggle}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggle(e); }}
          className="inline-flex cursor-pointer align-middle"
          data-testid="verified-badge-trigger"
        >
          {icon}
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-3"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        data-testid="verified-badge-popover"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} style={{ color: meta.color }} />
          <div className="font-semibold text-sm" data-testid="verified-badge-popover-role">{roleLabel}</div>
        </div>
        <p className="text-xs mt-2" style={{ color: "var(--yalola-text-muted)" }}>
          {t("badge.popover_desc", "Compte vérifié sur Yalola. La couleur du badge indique le rôle du compte.")}
        </p>
      </PopoverContent>
    </Popover>
  );
};
